<template>
  <div class="template-files">
    <!-- 拖拽/点击上传区域 -->
    <div
      v-if="!readonly"
      class="upload-drop-zone"
      :class="{ 'is-dragover': isDragover }"
      @click="triggerFileInput"
      @drop.prevent="onDrop"
      @dragover.prevent="isDragover = true"
      @dragleave.prevent="isDragover = false"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden-input"
        @change="onFileInputChange"
      />
      <i class="el-icon-upload upload-icon"></i>
      <div class="upload-text">将文件拖到此处，或<em>点击上传</em></div>
      <div class="upload-tip">支持图片、文档等多种格式，可批量上传</div>
    </div>

    <!-- 文件列表（缩略图网格） -->
    <div v-if="innerFiles.length > 0" class="file-grid">
      <div
        v-for="(file, index) in innerFiles"
        :key="index"
        class="file-card"
        :class="{ 'is-uploading': file.uploading }"
      >
        <!-- 上传中遮罩 -->
        <div v-if="file.uploading" class="upload-mask">
          <i class="el-icon-loading"></i>
          <span>上传中...</span>
        </div>
        <!-- 缩略图区域 -->
        <div class="file-thumb" @click="handlePreview(file)">
          <img v-if="file.thumbUrl" :src="file.thumbUrl" class="thumb-img" />
          <div v-else class="thumb-icon">
            <i :class="getFileIcon(file.name)" class="file-type-icon"></i>
            <span class="file-ext">{{ getFileExt(file.name) }}</span>
          </div>
        </div>
        <!-- 文件信息 -->
        <div class="file-info">
          <span class="file-name" :title="file.name">{{ file.name }}</span>
          <span v-if="file.size" class="file-size">{{ formatSize(file.size) }}</span>
        </div>
        <!-- 操作栏 -->
        <div class="file-actions">
          <i class="el-icon-zoom-in action-btn" title="预览" @click="handlePreview(file)"></i>
          <i class="el-icon-download action-btn" title="下载" @click="handleDownload(file)"></i>
          <i v-if="!readonly" class="el-icon-delete action-btn danger" title="删除" @click="handleRemove(index)"></i>
        </div>
      </div>
    </div>

    <!-- 无文件提示（只读模式） -->
    <div v-if="readonly && innerFiles.length === 0" class="empty-tip">
      <i class="el-icon-folder-opened"></i>
      <span>暂无附件</span>
    </div>

    <!-- 图片预览弹窗（带旋转、翻页） -->
    <el-dialog
      :visible.sync="previewVisible"
      :title="previewTitle"
      width="80%"
      append-to-body
      custom-class="preview-dialog"
      @close="onPreviewClose"
    >
      <div class="preview-wrapper">
        <!-- 左翻页 -->
        <div
          v-if="previewMode === 'image'"
          class="nav-arrow nav-left"
          :class="{ disabled: !hasPrevImage }"
          @click="prevImage"
        >
          <i class="el-icon-arrow-left"></i>
        </div>

        <!-- 预览主体 -->
        <div
          class="preview-body"
          :class="{ 'preview-body-pdf': previewMode === 'pdf', 'preview-body-text': previewMode === 'text' }"
          @mousedown="previewMode === 'image' ? onDragStart($event) : null"
          @wheel.prevent="previewMode === 'image' ? onWheel($event) : null"
        >
          <!-- 加载中 -->
          <div v-if="previewLoading" class="preview-unsupported">
            <i class="el-icon-loading" style="font-size:40px;color:#409eff;"></i>
            <p>正在加载预览...</p>
          </div>
          <!-- 图片预览 -->
          <img
            v-else-if="previewMode === 'image' && currentPreviewUrl"
            ref="previewImg"
            :src="currentPreviewUrl"
            class="preview-img"
            :style="previewImgStyle"
            @dragstart.prevent
          />
          <!-- PDF 预览（含 Office 转 PDF） -->
          <iframe
            v-else-if="previewMode === 'pdf' && pdfBlobUrl"
            :src="pdfBlobUrl"
            class="preview-pdf"
            frameborder="0"
          ></iframe>
          <!-- 文本预览 -->
          <div v-else-if="previewMode === 'text' && textContent" class="preview-text-wrap">
            <pre class="preview-text">{{ textContent }}</pre>
          </div>
          <!-- 不支持 -->
          <div v-else class="preview-unsupported">
            <i class="el-icon-document" style="font-size:60px;color:#c0c4cc;"></i>
            <p>该文件类型不支持在线预览</p>
            <el-button type="primary" size="small" icon="el-icon-download" @click="handleDownload(previewFile)">下载查看</el-button>
          </div>
        </div>

        <!-- 右翻页 -->
        <div
          v-if="previewMode === 'image'"
          class="nav-arrow nav-right"
          :class="{ disabled: !hasNextImage }"
          @click="nextImage"
        >
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>

      <!-- 底部工具栏 -->
      <div v-if="previewMode === 'image'" class="preview-toolbar">
        <div class="toolbar-btn" title="逆时针旋转" @click="rotateLeft"><i class="el-icon-refresh-left"></i></div>
        <div class="toolbar-btn" title="顺时针旋转" @click="rotateRight"><i class="el-icon-refresh-right"></i></div>
        <div class="toolbar-btn" title="水平翻转" @click="flipH"><i class="el-icon-sort"></i></div>
        <div class="toolbar-btn" title="垂直翻转" @click="flipV"><i class="el-icon-sort" style="transform:rotate(90deg)"></i></div>
        <div class="toolbar-sep"></div>
        <div class="toolbar-btn" title="放大" @click="zoomIn"><i class="el-icon-zoom-in"></i></div>
        <div class="toolbar-btn" title="缩小" @click="zoomOut"><i class="el-icon-zoom-out"></i></div>
        <div class="toolbar-btn" title="重置" @click="resetTransform"><i class="el-icon-refresh"></i></div>
        <div class="toolbar-sep"></div>
        <div class="toolbar-btn" title="下载" @click="handleDownload(previewFile)"><i class="el-icon-download"></i></div>
      </div>

      <!-- 图片计数 -->
      <div v-if="imageFiles.length > 1" class="preview-counter">
        {{ previewIndex + 1 }} / {{ imageFiles.length }}
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { uploadFiles } from '../../api/workflow'
import { downloadUploadedFile, formatDownloadError, getPdfPreviewBlob, getTextPreview } from '../../api/upload'

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml']
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']

export default {
  name: 'TemplateFiles',
  props: {
    value: { default: null },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      innerFiles: [],    // { id, name, size, url, thumbUrl, uploading }
      isDragover: false,
      previewVisible: false,
      previewFile: null,
      previewIndex: -1,
      previewMode: 'image',  // 'image' | 'pdf' | 'text'
      pdfBlobUrl: '',
      textContent: '',
      previewLoading: false,
      // 图片变换状态
      rotate: 0,
      scaleX: 1,
      scaleY: 1,
      zoom: 1,
      // 拖拽平移状态
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragBaseX: 0,
      dragBaseY: 0
    }
  },
  computed: {
    // 所有图片文件
    imageFiles() {
      return this.innerFiles.filter(f => this.isImageFile(f))
    },
    hasPrevImage() {
      return this.previewIndex > 0
    },
    hasNextImage() {
      return this.previewIndex < this.imageFiles.length - 1
    },
    currentPreviewUrl() {
      if (this.previewFile && this.previewFile.thumbUrl) return this.previewFile.thumbUrl
      return ''
    },
    previewTitle() {
      if (!this.previewFile) return '预览'
      return this.previewFile.name
    },
    previewImgStyle() {
      const transition = this.isDragging ? 'none' : 'transform 0.3s ease'
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) rotate(${this.rotate}deg) scale(${this.scaleX * this.zoom}, ${this.scaleY * this.zoom})`,
        transition
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && val.length > 0 && this.innerFiles.length === 0) {
          this.innerFiles = val.map(f => {
            if (typeof f === 'string') return { id: null, name: f, size: 0, url: '', thumbUrl: '', uploading: false }
            const url = f.url || ''
            const thumbUrl = (url && this.isImageByExt(f.name)) ? url : ''
            return { id: f.id || null, name: f.name || '', size: f.size || 0, url: url, thumbUrl: thumbUrl, uploading: false }
          })
        }
      }
    }
  },
  methods: {
    // ========== 上传 ==========
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    onFileInputChange(e) {
      const files = Array.from(e.target.files || [])
      this.addFiles(files)
      // 清空 input 让同一文件可重复选择
      this.$refs.fileInput.value = ''
    },
    onDrop(e) {
      this.isDragover = false
      const files = Array.from(e.dataTransfer.files || [])
      this.addFiles(files)
    },
    addFiles(rawFiles) {
      // 先在列表中显示为"上传中"状态
      const startIndex = this.innerFiles.length
      rawFiles.forEach(rawFile => {
        const fileObj = {
          id: null,
          name: rawFile.name,
          size: rawFile.size,
          url: '',
          thumbUrl: this.isImage(rawFile) ? URL.createObjectURL(rawFile) : '',
          uploading: true
        }
        this.innerFiles.push(fileObj)
      })

      // 调用上传接口
      uploadFiles(rawFiles).then(res => {
        const uploaded = res.data || []
        uploaded.forEach((item, i) => {
          const idx = startIndex + i
          if (idx < this.innerFiles.length) {
            // 释放旧的临时 blob URL
            if (this.innerFiles[idx].thumbUrl && this.innerFiles[idx].thumbUrl.startsWith('blob:')) {
              URL.revokeObjectURL(this.innerFiles[idx].thumbUrl)
            }
            this.$set(this.innerFiles, idx, {
              id: item.id,
              name: item.name,
              size: item.size,
              url: item.url,
              thumbUrl: this.isImageByExt(item.name) ? item.url : '',
              uploading: false
            })
          }
        })
        this.emitValue()
      }).catch(() => {
        // 上传失败，移除这些项
        this.innerFiles.splice(startIndex, rawFiles.length)
        this.$message.error('文件上传失败')
      })
    },

    // ========== 删除 ==========
    handleRemove(index) {
      this.innerFiles.splice(index, 1)
      this.emitValue()
    },

    // ========== 预览 ==========
    async handlePreview(file) {
      if (this.isImageFile(file)) {
        const idx = this.imageFiles.indexOf(file)
        this.previewIndex = idx > -1 ? idx : 0
        this.previewFile = file
        this.resetTransform()
        this.previewMode = 'image'
        this.previewVisible = true
        return
      }
      // 判断文件类型
      const ext = this.getFileExt(file.name).toLowerCase()
      const pdfExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf']
      const textExts = ['txt', 'md', 'markdown', 'csv', 'log', 'json', 'xml', 'yml', 'yaml']
      if (pdfExts.includes(ext)) {
        // PDF 或 Office 文档
        this.previewFile = file
        this.previewMode = 'pdf'
        this.previewLoading = true
        this.pdfBlobUrl = ''
        this.previewVisible = true
        try {
          const blob = await getPdfPreviewBlob(file.id)
          this.pdfBlobUrl = URL.createObjectURL(blob)
        } catch (e) {
          this.$message.error(e.message || '预览失败')
          this.previewVisible = false
        }
        this.previewLoading = false
      } else if (textExts.includes(ext)) {
        // 文本文件
        this.previewFile = file
        this.previewMode = 'text'
        this.previewLoading = true
        this.textContent = ''
        this.previewVisible = true
        try {
          this.textContent = await getTextPreview(file.id)
        } catch (e) {
          this.$message.error(e.message || '读取文本失败')
          this.previewVisible = false
        }
        this.previewLoading = false
      } else {
        // 不支持预览的文件：下载
        this.handleDownload(file)
      }
    },
    prevImage() {
      if (!this.hasPrevImage) return
      this.previewIndex--
      this.previewFile = this.imageFiles[this.previewIndex]
      this.resetTransform()
    },
    nextImage() {
      if (!this.hasNextImage) return
      this.previewIndex++
      this.previewFile = this.imageFiles[this.previewIndex]
      this.resetTransform()
    },
    onPreviewClose() {
      this.resetTransform()
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl)
        this.pdfBlobUrl = ''
      }
      this.textContent = ''
      this.previewMode = 'image'
    },

    // ========== 图片变换 ==========
    rotateLeft() {
      this.rotate = (this.rotate - 90) % 360
    },
    rotateRight() {
      this.rotate = (this.rotate + 90) % 360
    },
    flipH() {
      this.scaleX = this.scaleX * -1
    },
    flipV() {
      this.scaleY = this.scaleY * -1
    },
    zoomIn() {
      this.zoom = Math.min(this.zoom + 0.25, 5)
    },
    zoomOut() {
      this.zoom = Math.max(this.zoom - 0.25, 0.25)
    },
    resetTransform() {
      this.rotate = 0
      this.scaleX = 1
      this.scaleY = 1
      this.zoom = 1
      this.translateX = 0
      this.translateY = 0
    },
    // ========== 鼠标拖拽平移 ==========
    onDragStart(e) {
      // 只响应左键
      if (e.button !== 0) return
      this.isDragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragBaseX = this.translateX
      this.dragBaseY = this.translateY
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },
    onDragMove(e) {
      if (!this.isDragging) return
      this.translateX = this.dragBaseX + (e.clientX - this.dragStartX)
      this.translateY = this.dragBaseY + (e.clientY - this.dragStartY)
    },
    onDragEnd() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
    },

    // ========== 鼠标滚轮缩放 ==========
    onWheel(e) {
      if (e.deltaY < 0) {
        this.zoom = Math.min(this.zoom + 0.1, 5)
      } else {
        this.zoom = Math.max(this.zoom - 0.1, 0.25)
      }
    },

    handleKeydown(e) {
      if (!this.previewVisible) return
      if (e.key === 'ArrowLeft') { this.prevImage() }
      else if (e.key === 'ArrowRight') { this.nextImage() }
      else if (e.key === 'Escape') { this.previewVisible = false }
    },

    // ========== 下载（须带 JWT：直接 <a href> 会 401，浏览器报无法提取文件）==========
    async handleDownload(file) {
      if (!file) return
      if (file.id) {
        try {
          await downloadUploadedFile(file.id, file.name)
        } catch (e) {
          const msg = await formatDownloadError(e)
          this.$message.error(msg)
        }
      } else if (file.url) {
        window.open(file.url, '_blank')
      }
    },

    // ========== 工具方法 ==========
    isImage(rawFile) {
      if (rawFile.type && IMAGE_TYPES.includes(rawFile.type)) return true
      return this.isImageByExt(rawFile.name)
    },
    isImageFile(file) {
      if (file.thumbUrl) return true
      return this.isImageByExt(file.name)
    },
    isImageByExt(name) {
      if (!name) return false
      const lower = name.toLowerCase()
      return IMAGE_EXTS.some(ext => lower.endsWith(ext))
    },
    getFileExt(name) {
      if (!name) return ''
      const idx = name.lastIndexOf('.')
      return idx > -1 ? name.substring(idx + 1).toUpperCase() : ''
    },
    getFileIcon(name) {
      const ext = this.getFileExt(name).toLowerCase()
      const iconMap = {
        pdf: 'el-icon-document',
        doc: 'el-icon-document',
        docx: 'el-icon-document',
        xls: 'el-icon-document',
        xlsx: 'el-icon-document',
        ppt: 'el-icon-document',
        pptx: 'el-icon-document',
        zip: 'el-icon-files',
        rar: 'el-icon-files',
        '7z': 'el-icon-files',
        txt: 'el-icon-tickets',
        mp4: 'el-icon-video-camera',
        avi: 'el-icon-video-camera',
        mp3: 'el-icon-headset',
        wav: 'el-icon-headset'
      }
      return iconMap[ext] || 'el-icon-document'
    },
    formatSize(bytes) {
      if (!bytes || bytes === 0) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    emitValue() {
      // 只输出已上传成功的文件信息（可被 JSON 序列化）
      const uploaded = this.innerFiles.filter(f => f.id && !f.uploading)
      this.$emit('input', uploaded.map(f => ({
        id: f.id,
        name: f.name,
        size: f.size,
        url: f.url
      })))
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('mousemove', this.onDragMove)
    document.removeEventListener('mouseup', this.onDragEnd)
  }
}
</script>

<style scoped>
.template-files {
  width: 100%;
}

/* ===== 上传拖拽区 ===== */
.upload-drop-zone {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 28px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.25s;
  background: #fafafa;
}
.upload-drop-zone:hover {
  border-color: #409eff;
  background: #f0f7ff;
}
.upload-drop-zone.is-dragover {
  border-color: #409eff;
  background: #ecf5ff;
}
.upload-icon {
  font-size: 40px;
  color: #c0c4cc;
  display: block;
  margin-bottom: 8px;
}
.upload-drop-zone:hover .upload-icon,
.upload-drop-zone.is-dragover .upload-icon {
  color: #409eff;
}
.upload-text {
  font-size: 14px;
  color: #606266;
}
.upload-text em {
  color: #409eff;
  font-style: normal;
}
.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
.hidden-input {
  display: none;
}

/* ===== 文件网格 ===== */
.file-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}
.file-card {
  width: 120px;
  border: 1px solid #e6ebf5;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}
.file-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}
.file-card.is-uploading {
  opacity: 0.6;
  pointer-events: none;
  position: relative;
}
.upload-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  font-size: 12px;
  color: #409eff;
  gap: 4px;
}
.upload-mask i {
  font-size: 20px;
}

/* 缩略图 */
.file-thumb {
  width: 100%;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  cursor: pointer;
  overflow: hidden;
}
.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.file-type-icon {
  font-size: 32px;
  color: #909399;
}
.file-ext {
  font-size: 11px;
  color: #909399;
  font-weight: 600;
  text-transform: uppercase;
}

/* 文件信息 */
.file-info {
  padding: 6px 8px 2px;
  flex: 1;
}
.file-name {
  font-size: 12px;
  color: #303133;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}
.file-size {
  font-size: 11px;
  color: #909399;
  display: block;
  line-height: 1.4;
}

/* 操作栏 */
.file-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 6px 8px;
  border-top: 1px solid #f0f2f5;
}
.action-btn {
  font-size: 15px;
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;
}
.action-btn:hover {
  color: #409eff;
}
.action-btn.danger:hover {
  color: #f56c6c;
}

/* ===== 空状态 ===== */
.empty-tip {
  color: #909399;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 0;
}

/* 弹窗外层由下方「非 scoped」块控制居中；此处仅预览区内部布局（slot 仍带 scoped） */
.preview-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  min-height: 420px;
  background: #f5f7fa;
}
.preview-body {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 420px;
  max-height: 70vh;
  overflow: hidden;
  padding: 24px 56px;
  cursor: grab;
}
.preview-body:active {
  cursor: grabbing;
}
.preview-img {
  max-width: 100%;
  max-height: 65vh;
  user-select: none;
  -webkit-user-drag: none;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
}
.preview-unsupported {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}
.preview-unsupported p {
  margin: 16px 0;
  font-size: 14px;
}

/* 左右翻页箭头 */
.nav-arrow {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  color: #606266;
  transition: all 0.2s;
  flex-shrink: 0;
  z-index: 10;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.nav-left {
  left: 14px;
}
.nav-right {
  right: 14px;
}
.nav-arrow:hover {
  background: #409eff;
  color: #fff;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
}
.nav-arrow.disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.nav-arrow.disabled:hover {
  background: #fff;
  color: #606266;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 底部工具栏 */
.preview-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 10px 0;
  background: #fff;
  border-top: 1px solid #ebeef5;
}
.toolbar-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  color: #606266;
  font-size: 18px;
  transition: all 0.2s;
}
.toolbar-btn:hover {
  background: #ecf5ff;
  color: #409eff;
}
.toolbar-sep {
  width: 1px;
  height: 20px;
  background: #dcdfe6;
  margin: 0 6px;
}

/* 图片计数 */
.preview-counter {
  text-align: center;
  padding: 4px 0 8px;
  font-size: 13px;
  color: #909399;
  background: #fff;
}
/* PDF 预览 iframe */
.preview-pdf {
  width: 100%;
  height: 65vh;
  min-height: 400px;
  border: none;
  border-radius: 4px;
}
.preview-body-pdf {
  padding: 16px !important;
  cursor: default;
}
/* 文本预览 */
.preview-text-wrap {
  width: 100%;
  height: 65vh;
  min-height: 400px;
  overflow: auto;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e6ebf5;
}
.preview-text {
  margin: 0;
  padding: 16px 20px;
  font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-all;
}
.preview-body-text {
  padding: 16px !important;
  cursor: default;
}

</style>

<!-- append-to-body 后弹窗在 body 下，必须用非 scoped 才能作用到 .el-dialog__wrapper -->
<style lang="scss">
/* 预览遮罩：视口内居中。禁止对 display 使用 !important，否则会压过关闭时内联的 display:none，导致只残留标题条 */
.el-dialog__wrapper:has(.el-dialog.preview-dialog) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-dialog.preview-dialog {
  margin: 0 !important;
  position: relative !important;
  top: 0 !important;
  left: 0 !important;
  transform: none !important;
  border-radius: 10px;
  overflow: hidden;
  max-height: calc(100vh - 48px);
  width: 80% !important;
  max-width: 960px;
  display: flex !important;
  flex-direction: column !important;
}

.el-dialog.preview-dialog .el-dialog__header {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  padding: 14px 20px;
}

.el-dialog.preview-dialog .el-dialog__title {
  font-size: 15px;
  color: #303133;
}

.el-dialog.preview-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 !important;
}

/* Chrome 浏览器 :has；若不可用则仍为 Element 默认顶距，影响不大 */
</style>
